import Vue from 'vue';
import Component from 'vue-class-component';
import { MeetingsService } from '@/meetings/services/meetings.service';
import { User } from '../../core/models/user.model';
import KEYS from '@/values/keys';
import { MEETING_EVENTS, _MEETINGS_STORE } from '@/meetings/values/meetings.values';
import MeetingUserConnectedResponse from '@/meetings/models/meeting-user-connected-response.model';
import MeetingInformationResponse from '@/meetings/models/meeting-information-response.model';
import { LOADING_EVENTS } from '@/core/values/core.values';
import { GUEST_TACHER_ROLE, PRESENTATION_MODE_EVENTS, EVENT_TYPES, SOCKET_EVENTS } from '@/content/values/ed-presentation-mode.values';
import WebSocketMessageResponse from '@/content/models/web-socket-message-response.model';
import WebSocketMessageRequest from '@/content/models/web-socket-message-request.model';
import EdPresentationModeFactory from '@/content/factories/ed-presentation-mode.factory';
import { ED_MODAL_EVENTS } from '@/plugins/ed-modal/ed-modal.plugin';


@Component({})
export class PresentationModeSessionMixin extends Vue {

  get userInfo(): User {
    return this.$store.state.AuthStore.user;
  }

  get isPublisher(): boolean {
    return this.$store.state.AuthStore.role === KEYS.ROLES.PUBLISHER;
  }

  get isStudent() : boolean {
    return this.$store.state.AuthStore.role === KEYS.ROLES.BUYER;
  }

  get isShowingChangePresenter() : boolean {
    return this.$store.state.MeetingsStore.isShowingChangePresenter;
  }

  get isWebview() : boolean {
    return this.$store.state.AppStore.webview;
  }

  public validateSession(meetingId : number, isReconnect : boolean) : Promise<boolean> {
    this.showLoading();
    return new Promise((resolve) => {

      if(!this.isPublisher){
        this.hideLoading();
        return resolve(true);
      }

      this.validatePresentationModeSession(meetingId).then(async (result : MeetingUserConnectedResponse) => {
        this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_OTHER_SESSION, result.alreadyConnected);
        if(result.alreadyConnected) {
          this.showOtherUserSession(result);
          this.hideLoading();
          return resolve(false);
        }
  

        let response : MeetingInformationResponse = await this.validatePresentationModeTeacherSession(meetingId);
        this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_ACTIVE_PRESENTER_TEACHER, response.isActive);
        this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_PRESENTER_TEACHER_INFORMATION, response);
        this.hideLoading();
        if(response.isActive){
          if(!isReconnect){
            this.showOtherTeacherSession(resolve, response);
          } else {
            window.location.reload();
          }
          return;
        } else {
          this.hideLoading();
        }
        this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_VALIDATE_PRESENTATION_SESSSION, true);
        resolve(true);
      }).catch(() => resolve(false));
    });
  }

  public showLoading() : void {
    this.$edEventBus.$emit(LOADING_EVENTS.SHOW);
  }

  public hideLoading() : void {
    this.$edEventBus.$emit(LOADING_EVENTS.HIDE);
  }

  public validateGuestUserMessage(message : WebSocketMessageResponse, roomId : number) : void {
    if(message && message.messageType === EVENT_TYPES.changePresenter && message.topicId.toString() === roomId.toString()){
      if(this.isShowingChangePresenter) {
        return;
      }
      this.showChangePresenter(message.topicId, message.messageAttempt);
      return;
    }
    if(this.isShowingChangePresenter) {
      this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_SHOWING_CHANGE_PRESENTER, false);
      this.$EdModal.$emit(ED_MODAL_EVENTS.ED_MODAL_CONFIRMATION_HIDE);
    }
    this.$edEventBus.$emit(PRESENTATION_MODE_EVENTS.LOAD_ALL_MESSAGES, message);
    this.$edEventBus.$emit(MEETING_EVENTS.ALL_USER_MESSAGES, message);
  }

  private async validatePresentationModeSession(meetingId: number) : Promise<MeetingUserConnectedResponse> {
    try {
      return await MeetingsService.validatePresentationModeSession(meetingId, this.userInfo.userId);
    } catch(error) {
      this.showErrorMessage();
      this.hideLoading();
      throw error;
    }
  }

  public async validatePresentationModeTeacherSession(meetingId: number) : Promise<MeetingInformationResponse> {
    try {
      let response : MeetingInformationResponse = await MeetingsService.getPresentationModeMeetingInformation(meetingId);
      return response;
    } catch(error) {
      this.showErrorMessage();
      this.hideLoading();
      throw error;
    }
  }

  private showOtherUserSession(result : MeetingUserConnectedResponse) : void {
    let message = this.$t('meetings.presentationMode.otherUserSession').toString();
    if (result.time && result.device) {
      message += '\n' + this.$t('meetings.presentationMode.otherUserSessionDetails').toString()
          .replace('{date}', new Date(result.time).toLocaleString())
          .replace('{device}', result.device.replaceAll('|', ' '));
    }
    this.$EdModal.createMessageModal({
      title: this.$t('common.information'),
      body: message,
      action: () => this.$router.push('/'),
      type: KEYS.EVENT_TYPES.WARNING,
      showAction: !this.isWebview
    });
  }

  private showOtherTeacherSession(resolve : Function, response : MeetingInformationResponse) : void {
    this.$EdModal.createConfirmationModal({
      body: this.$t('meetings.presentationMode.teacherAsGuest').toString().replace('{name}', response.presenterName),
      actions: [
        {
          label: this.$t('common.accept'),
          action: () => {
            this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_PRESENTATION_MODE_GUEST_TEACHER, true);
            this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_VALIDATE_PRESENTATION_SESSSION, true);
            resolve(true);
          }
        },
        {
          label: this.$t('common.cancel'),
          mode: 'outline',
          action: () => {
            this.$router.push('/');
            resolve(false);
          },
          showAction: !this.isWebview
        }
      ]
    });
  }

  private showErrorMessage() : void {
    this.$EdModal.createMessageModal({
      title: this.$t('common.information'),
      body: this.$t('errors.errorLoading'),
      type: KEYS.EVENT_TYPES.WARNING,
      action: () => this.$router.push('/')
    });
  }

  private showChangePresenter(roomId : number, messageAttempt : number) : void {
    this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_SHOWING_CHANGE_PRESENTER, true);
    this.$EdModal.createConfirmationModal({
      body: this.$t('meetings.presentationMode.hostTeacherLeftSession'),
      actions: [
        {
          label: this.$t('common.accept'),
          action: () => window.location.reload()
        },
        {
          label: this.$t('common.wait'),
          mode: 'outline',
          action: () => {
            this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_SHOWING_CHANGE_PRESENTER, false);
            let message: WebSocketMessageRequest = EdPresentationModeFactory.createWebSocketMessageRequestWithUserType(
              EVENT_TYPES.userConnect, String(roomId), GUEST_TACHER_ROLE, false, '', '', messageAttempt);
            this.$EdSocket.emit(SOCKET_EVENTS.guestTeacherDeclinePresenter, message);
          }
        }
      ],
      onFront: true,
    });
  }
}